import React, { useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';

// Types
import { Props, TeamManagement } from './types';

// Schema
import { TeamManagementSchema } from './schema';

// Styles
import { Container, InputsContainer, AddTeamButton } from './styles';

// Components
import InputText from '../../inputs/InputText';
import DropDownInput from '../../inputs/DropDown/DropDownInput';

// Utils
import { useCacheFormInSessionStorage } from '../../../hooks/useCacheForm';
import { Typography } from '../../display/Typography';
import { ImperativeRefInput } from '../../inputs/DropDown/DropDownInput/types';
import { Options } from '../../inputs/DropDown/DropDownList/types';

export const AddTeamForm = ({
    onFormSubmit,
    namingPersistForm,
    initialData,
}: Props) => {
    const { t } = useTranslation(['forms', 'management']);
    const {
        handleSubmit,
        control,
        register,
        formState: { errors },
        reset,
    } = useForm<TeamManagement>({
        defaultValues: {
            ...initialData,
            concessionIDs: [],
            roadIDs: [],
            userIDs: [],
            vehicleIDs: [],
        },
        resolver: yupResolver(TeamManagementSchema) as any,
    });

    useCacheFormInSessionStorage(namingPersistForm, control);

    // Refs
    const concessionRef = useRef<ImperativeRefInput>(null);

    // States
    const [nameError, setNameError] = useState<null | string>(null);

    const onSubmit = async (data: TeamManagement) => {
        const errorMessage = await onFormSubmit(data);
        if (errorMessage) {
            setNameError(errorMessage);
        } else {
            setNameError(null);
            reset({
                name: '',
                concessionIDs: [],
                roadIDs: [],
                userIDs: [],
                vehicleIDs: [],
            });
            concessionRef.current?.clearSelected();
        }
    };

    // Concession options
    const concessionOptions: Options =
        initialData?.concessions?.map((c) => ({
            label: c.name,
            value: c.id,
        })) || [];

    return (
        <Container onSubmit={handleSubmit(onSubmit)}>
            <Typography size="L" variant="semiBold">
                {t('management:addTeam')}
            </Typography>

            <InputsContainer>
                <InputText
                    {...register('name')}
                    label={t('common:labels.name', 'Nombre')}
                    typeInput={'normal'}
                    inputSize="M"
                    placeholder={t('management:teams.namePlaceholder', 'Nombre del equipo')}
                    borderError={!!errors.name?.message}
                />
                <Controller
                    control={control}
                    name="concessionIDs"
                    render={({ field: { onChange, value }, formState: { defaultValues } }) => {
                        // Si solo hay una concesión, auto-seleccionar y no mostrar el selector
                        if (concessionOptions.length === 1) {
                            const concessionId = concessionOptions[0].value;
                            if (!value || value.length === 0 || value[0] !== concessionId) {
                                onChange([concessionId]);
                            }
                            return <></>;
                        }
                        return (
                            <DropDownInput
                                placeholder={t('common:labels.select', 'Seleccionar')}
                                labelText={t('user.concession', 'Concesión')}
                                defaultValue={defaultValues?.concessionIDs?.[0] || ''}
                                inputSize={'M'}
                                typeDropDown={'Default'}
                                options={concessionOptions}
                                onChangeSelected={(option) => onChange(option?.value ? [option.value] : [])}
                                borderError={!!errors.concessionIDs?.message}
                                ref={concessionRef}
                            />
                        );
                    }}
                />
                <AddTeamButton type="submit">⮕</AddTeamButton>
                {nameError && !errors.name?.message && (
                    <Typography colorText="lightCoralRed" size="XS">
                        {nameError}
                    </Typography>
                )}
            </InputsContainer>
        </Container>
    );
};
